package br.com.htms.inspeccaocampo.ui

import br.com.htms.inspeccaocampo.data.FormChecklistAlojamento
import br.com.htms.inspeccaocampo.data.FormComunicacaoOcorrencias
import br.com.htms.inspeccaocampo.data.FormInspecao
import br.com.htms.inspeccaocampo.data.FormRelatorioFoto
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr

/** Linhas de prévia (rótulo → valor) antes de salvar. */
object FormPrevia {

    fun inspecaoCampo(form: FormInspecao): List<Pair<String, String>> {
        val ok = form.itens.count { it.resposta != null }
        val fotos = form.itens.sumOf { it.fotos.size } +
            form.itensExtra.sumOf { it.fotos.size } +
            form.desviosEncontrados.fotos.size +
            form.acoesCorretivas.fotos.size +
            form.informacoesAdicionaisFotos.size
        val extrasPreenchidos = form.itensExtra.count { it.temConteudo() }
        return listOf(
            "Tipo" to "Inspeção de Campo Diária",
            "TST / Responsável" to form.tstResponsavel,
            "Obra" to form.obra,
            "Local" to form.localTexto,
            "Efetivo" to form.efetivo,
            "Data/hora" to if (form.dataHorarioMs > 0) formatDateTimeBr(form.dataHorarioMs) else "",
            "Verificações" to "$ok / ${form.itens.size} respondidas",
            "Itens extras" to if (extrasPreenchidos == 0) "—" else
                form.itensExtra.mapIndexedNotNull { i, p ->
                    if (p.temConteudo()) p.textoExportacao(i) else null
                }.joinToString("; "),
            "Fotos" to "$fotos anexo(s)",
            "Assinatura supervisor" to if (form.assinaturaSupervisor.isNotBlank()) "Sim" else "Não",
            "Assinatura SESMT" to if (form.assinaturaSesmt.isNotBlank()) "Sim" else "Não"
        )
    }

    fun relatorioFoto(form: FormRelatorioFoto): List<Pair<String, String>> {
        val ok = form.itens.count { it.resposta != null }
        val fotos = form.itens.sumOf { it.fotos.size } + form.observacoesFotos.size
        return listOf(
            "Tipo" to "Relatório Fotográfico — Alojamento",
            "Responsável" to form.responsavelAlojamento,
            "Obra" to form.obra,
            "Endereço" to form.endereco,
            "Capacidade / funcionários" to
                listOf(form.capacidadeTotalPessoas, form.quantidadeFuncionarios)
                    .filter { it.isNotBlank() }.joinToString(" / "),
            "Itens" to "$ok / ${form.itens.size} respondidos",
            "Fotos" to "$fotos anexo(s)"
        )
    }

    fun checklist(form: FormChecklistAlojamento): List<Pair<String, String>> {
        val ok = form.itens.count { it.resposta != null }
        val nc = form.itens.count { it.resposta == RespostaConformidade.NC }
        return listOf(
            "Tipo" to "Checklist — Alojamento",
            "Inspecionado por" to form.inspecionadoPor.nome,
            "Obra" to form.obra,
            "Endereço" to form.endereco,
            "Data" to listOf(form.dataInspecao, form.horaInspecao).filter { it.isNotBlank() }.joinToString(" "),
            "Itens" to "$ok / ${form.itens.size} respondidos (${nc} NC)",
            "Fotos" to "${form.itens.sumOf { it.fotos.size }} anexo(s)"
        )
    }

    fun vetores(form: FormChecklistAlojamento): List<Pair<String, String>> {
        val ok = form.itens.count { it.resposta != null }
        val nc = form.itens.count { it.resposta == RespostaConformidade.NC }
        val fotos = form.itens.sumOf { it.fotos.size }
        return listOf(
            "Tipo" to "Controle de vetores e pragas",
            "Obra" to form.obra,
            "Local do alojamento" to form.endereco,
            "Responsável pela inspeção" to form.inspecionadoPor.nome,
            "Responsável pelo alojamento" to form.acompanhadoPor.nome,
            "Trabalhadores" to form.numeroTrabalhadores,
            "Itens" to "$ok / ${form.itens.size} respondidos (${nc} não conforme)",
            "Fotos" to "$fotos anexo(s)"
        )
    }

    fun relatorioAlojamento(
        relatorio: FormRelatorioFoto,
        checklist: FormChecklistAlojamento
    ): List<Pair<String, String>> =
        listOf("Tipo" to "Relatório Alojamento (Parte 1 + Parte 2)") +
            relatorioFoto(relatorio).filter { it.first != "Tipo" } +
            checklist(checklist).filter { it.first != "Tipo" }.map { "Parte 2 — ${it.first}" to it.second }

    fun ocorrencias(form: FormComunicacaoOcorrencias): List<Pair<String, String>> = listOf(
        "Tipo" to "Comunicação Prévia de ocorrências",
        "Quem reporta" to form.quemReportando,
        "Obra" to form.obra,
        "Local" to form.localExato.ifBlank { form.localAcidente },
        "Responsável atividade" to form.responsavelAtividade,
        "Descrição" to form.descricaoEvento.take(200),
        "Fotos" to "${form.fotos.size} anexo(s)"
    )

    fun checklistDiario(
        form: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario
    ): List<Pair<String, String>> {
        val ok = form.itens.count { it.resposta != null }
        val nc = form.itens.count { it.resposta == RespostaConformidade.NC }
        return listOf(
            "Tipo" to form.marcaEnum().titulo,
            "Checklist" to form.tipoNome,
            "Preenchido por" to form.preenchidoPor,
            "Obra" to form.obra,
            "Placa / modelo" to listOf(form.placa, form.modelo).filter { it.isNotBlank() }.joinToString(" / "),
            "Motorista" to form.motoristaNome,
            "Itens" to "$ok / ${form.itens.size} respondidos (${nc} NC)",
            "Fotos" to "${form.totalFotosAnexos()} anexo(s)"
        )
    }
}
